import type { AuditLog, EmailTemplate, Notification, Referee, Resume } from '@/types';

const ago = (h: number) => new Date(Date.now() - h * 3_600_000).toISOString();

export const mockResumes: Resume[] = [
  {
    id: 'r_1',
    doctorId: 'd_1',
    name: 'ED Registrar — Master CV',
    isDefault: true,
    originalFilename: 'Sarah-Chen-CV-Master.docx',
    parseStatus: 'parsed',
    completionScore: 84,
    summary:
      'PGY7 emergency medicine registrar with strong rural & retrieval experience. ACEM trainee. Trauma, paediatric and indigenous health interest.',
    workHistory: [
      {
        id: 'wh1',
        organisation: 'Royal Northern Hospital',
        role: 'Senior ED Registrar',
        city: 'Sydney',
        state: 'NSW',
        startDate: '2024-02-01',
        endDate: null,
        current: true,
        description: 'Lead trainee on resus floor. Departmental representative on M&M committee.',
      },
      {
        id: 'wh2',
        organisation: 'Cairns & Hinterland HHS',
        role: 'PGY5 Rural Generalist Trainee',
        city: 'Cairns',
        state: 'QLD',
        startDate: '2022-02-01',
        endDate: '2023-12-31',
        description: 'Outreach clinics across Cape York, ED, anaesthetics term.',
      },
    ],
    credentials: [
      { id: 'c1', type: 'AHPRA Registration', issuer: 'Medical Board of Australia', reference: 'MED0001234567', effectiveDate: '2025-10-01', expiryDate: '2026-09-30' },
      { id: 'c2', type: 'ALS Provider', issuer: 'Australian Resuscitation Council', expiryDate: '2026-08-12' },
      { id: 'c3', type: 'EMST', issuer: 'RACS', effectiveDate: '2024-04-01' },
    ],
    clinicalSkills: ['Procedural sedation', 'POCUS (FAST, eFAST)', 'Central lines', 'Paediatric resus', 'Retrieval medicine'],
    academic: [
      { id: 'a1', qualification: 'MBBS (Hons)', institution: 'University of Sydney', yearAwarded: 2018 },
      { id: 'a2', qualification: 'BSc (Med)', institution: 'University of Sydney', yearAwarded: 2014 },
    ],
    extras: ['First-author publication in EMJ 2024', 'Volunteer EM teacher — RACMA mentoring'],
    createdAt: '2025-12-01T00:00:00Z',
    updatedAt: ago(48),
  },
];

export const mockReferees: Referee[] = [
  {
    id: 'ref_1',
    doctorId: 'd_1',
    fullName: 'Dr Helena Wright',
    position: 'ED Director',
    organisation: 'Royal Northern Hospital',
    email: 'h.wright@rnh.health.nsw.gov.au',
    phone: '+61 2 9000 1234',
    relationship: 'Direct supervisor',
    consentToContact: true,
    active: true,
    slot: 1,
    createdAt: ago(720),
    updatedAt: ago(48),
  },
  {
    id: 'ref_2',
    doctorId: 'd_1',
    fullName: 'A/Prof Tom Kessler',
    position: 'Director of Medical Services',
    organisation: 'Cairns & Hinterland HHS',
    email: 'tom.kessler@health.qld.gov.au',
    phone: '+61 7 4226 0000',
    relationship: 'Term supervisor (Rural Generalist program)',
    consentToContact: true,
    active: true,
    slot: 2,
    createdAt: ago(2100),
    updatedAt: ago(60),
  },
  {
    id: 'ref_3',
    doctorId: 'd_1',
    fullName: 'Dr Mae Lin',
    position: 'Anaesthetics Consultant',
    organisation: 'Cairns Hospital',
    email: 'mae.lin@cairns.org.au',
    relationship: 'Term supervisor (Anaesthetics rotation)',
    consentToContact: true,
    active: false,
    slot: 3,
    archivedAt: ago(120),
    createdAt: ago(2400),
    updatedAt: ago(120),
  },
];

export const mockNotifications: Notification[] = [
  { id: 'n1', userId: 'p_doctor_1', category: 'compliance', title: 'Police check verified', body: 'Your National Police Check has been verified by the compliance team.', read: false, createdAt: ago(2), link: '/portal/compliance' },
  { id: 'n2', userId: 'p_doctor_1', category: 'application', title: 'Presented to Royal Northern Hospital', body: 'Your application for ED Registrar (Sydney) has been presented to the hospital.', read: false, createdAt: ago(8), link: '/portal/applications' },
  { id: 'n3', userId: 'p_doctor_1', category: 'compliance', title: 'Indemnity certificate rejected', body: 'Please re-upload the certificate of currency, not the policy summary.', read: true, createdAt: ago(28), link: '/portal/compliance' },
];

export const mockAuditLogs: AuditLog[] = [
  { id: 'log1', actorUserId: 'p_compliance_1', actorName: 'Mark Dunlop', actorRole: 'compliance_admin', action: 'compliance_document.verified', category: 'compliance', severity: 'info', entityType: 'compliance_document', entityId: 'cd_1', createdAt: ago(8) },
  { id: 'log2', actorUserId: 'p_doctor_1',     actorName: 'Sarah Chen',  actorRole: 'doctor',            action: 'compliance_document.uploaded', category: 'compliance', severity: 'info', entityType: 'compliance_document', entityId: 'cd_2', createdAt: ago(48) },
  { id: 'log3', actorUserId: 'p_recruiter_1',  actorName: 'Lena Park',   actorRole: 'recruiter',         action: 'application.presented_to_hospital', category: 'application', severity: 'info', entityType: 'application', entityId: 'a_1', createdAt: ago(8) },
  { id: 'log4', actorUserId: 'p_compliance_1', actorName: 'Mark Dunlop', actorRole: 'compliance_admin', action: 'compliance_document.rejected', category: 'compliance', severity: 'warning', entityType: 'compliance_document', entityId: 'cd_4', oldValues: { status: 'pending_review' }, newValues: { status: 'rejected' }, createdAt: ago(30) },
  { id: 'log5', actorUserId: 'p_super_1',     actorName: 'Operations Lead', actorRole: 'super_admin', action: 'team.invited', category: 'team', severity: 'info', entityType: 'profile', entityId: 'p_recruiter_1', createdAt: ago(720) },
];

export const mockEmailTemplates: EmailTemplate[] = [
  {
    id: 'et_1',
    code: 'compliance_request',
    name: 'Compliance — request missing documents',
    subject: 'Action required: missing compliance documents',
    body: 'Hi {{doctor_name}},\n\nWe need the following documents to complete your compliance file ({{completion}}% complete):\n- {{status}}\n\nPlease upload via your portal: {{portal_url}}\n\nKind regards,\nLocum Network Compliance Team',
    placeholders: ['doctor_name', 'doctor_email', 'status', 'completion', 'ahpra_id', 'location', 'portal_url'],
    active: true,
    updatedAt: ago(72),
  },
  {
    id: 'et_2',
    code: 'offer_extended',
    name: 'Offer — extended to candidate',
    subject: 'Offer: {{job_title}} at {{hospital_name}}',
    body: 'Hi {{doctor_name}},\n\nWe are pleased to extend the following offer for {{job_title}} starting {{start_date}}.\n\nDaily rate: {{rate}}\n\nFull details available in your portal: {{portal_url}}\n\nLocum Network Recruitment',
    placeholders: ['doctor_name', 'job_title', 'hospital_name', 'start_date', 'rate', 'portal_url'],
    active: true,
    updatedAt: ago(48),
  },
  {
    id: 'et_3',
    code: 'checklist_request',
    name: 'Checklist — pre-placement request',
    subject: 'Pre-placement checklist for {{job_title}}',
    body: 'Hi {{doctor_name}},\n\nPlease complete the pre-placement checklist before your start date ({{start_date}}):\n{{portal_url}}\n\nLocum Network',
    placeholders: ['doctor_name', 'job_title', 'start_date', 'portal_url'],
    active: true,
    updatedAt: ago(96),
  },
  {
    id: 'et_4',
    code: 'compliance_expiry_reminder',
    name: 'Compliance — document expiring',
    subject: '{{doctor_name}}: your {{document_name}} expires in {{days_remaining}} days',
    body: 'Hi {{doctor_name}},\n\nYour {{document_name}} on file with Locum Network is due to expire on {{expiry_date}} — that\'s {{days_remaining}} days from today.\n\nTo keep your placements running, please upload a renewed copy in your portal:\n{{portal_url}}\n\nOnce received, our compliance team will verify it within 1 business day.\n\nKind regards,\nLocum Network Compliance Team',
    placeholders: ['doctor_name', 'document_name', 'expiry_date', 'days_remaining', 'portal_url'],
    active: true,
    updatedAt: ago(2),
  },
  {
    id: 'et_5',
    code: 'job_alert_digest',
    name: 'Job alerts — digest',
    subject: 'New {{search_name}} jobs on Locum Network ({{match_count}})',
    body: 'Hi {{doctor_name}},\n\nHere are the new placements matching your saved search "{{search_name}}":\n\n{{job_list}}\n\nView all matches:\n{{search_url}}\n\nYou can change the frequency or unsubscribe in your portal:\n{{alerts_url}}\n\nKind regards,\nLocum Network',
    placeholders: ['doctor_name', 'search_name', 'match_count', 'job_list', 'search_url', 'alerts_url'],
    active: true,
    updatedAt: ago(2),
  },
];
